/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { alpha } from '@mui/material/styles';

import { getCommandIntelligenceTokens } from '../../../theme/commandIntelligenceTokens';

/**
 * @param {import('@mui/material').Theme} theme
 * @param {'positive' | 'negative'} rating
 * @param {'positive' | 'negative' | null | undefined} existingFeedback
 */
export const getFeedbackButtonSx = (theme, rating, existingFeedback) => {
  const isSelected = existingFeedback === rating;
  const selectedColor = rating === 'positive' ? theme.palette.success.main : theme.palette.error.main;

  if (isSelected) {
    return {
      color: selectedColor,
      bgcolor: alpha(selectedColor, 0.12),
      '&:hover': {
        bgcolor: alpha(selectedColor, 0.16),
        color: selectedColor,
      },
      '&.Mui-disabled': {
        color: selectedColor,
        bgcolor: alpha(selectedColor, 0.12),
        opacity: 1,
      },
    };
  }

  return {
    color: 'text.secondary',
    '&:hover': {
      color: selectedColor,
    },
  };
};

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 */
export const getMessageItemStyles = theme => {
  const tokens = getCommandIntelligenceTokens(theme);

  return {
    row: isUser => ({
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      mb: 3,
      animation: 'messageFadeIn 180ms ease-out',
      '@keyframes messageFadeIn': {
        from: { opacity: 0, transform: 'translateY(4px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    }),
    userText: {
      maxWidth: { xs: '90%', sm: '72%' },
      px: 1.75,
      py: 1.1,
      borderRadius: tokens.radiusBubble,
      color: tokens.bubbleUserText,
      backgroundColor: tokens.bubbleUserBg,
      border: `1px solid ${tokens.bubbleUserBorder}`,
      whiteSpace: 'pre-wrap',
      lineHeight: 1.55,
      fontSize: '0.93rem',
      fontFamily: tokens.fontStack,
    },
    assistantText: {
      width: '100%',
      maxWidth: '100%',
      fontFamily: tokens.fontStack,
      color: tokens.textPrimary,
    },
    systemText: {
      maxWidth: { xs: '90%', sm: '72%' },
      px: 1.6,
      py: 0.95,
      borderRadius: tokens.radiusChip,
      alignSelf: 'flex-start',
      bgcolor: tokens.surfaceInput,
      border: `1px solid ${tokens.borderSubtle}`,
      color: tokens.textMuted,
      fontSize: '0.9rem',
      fontFamily: tokens.fontStack,
    },
    errorAlert: {
      borderRadius: tokens.radiusChip,
      bgcolor: tokens.statusErrorBg,
    },
    followUpChip: {
      borderRadius: tokens.radiusChip,
      borderColor: tokens.borderSubtle,
      color: tokens.accentPrimary,
      fontFamily: tokens.fontStack,
      '&:hover': {
        borderColor: tokens.accentPrimary,
        bgcolor: tokens.surfaceInput,
      },
    },
  };
};
